import apiClient from './apiClient';
import { SocialLink } from '../../type/socialLinks';
import { TalentMediaItem } from './talentMediaService';

export interface TalentSearchFilters {
	searchText?: string;
	maxHourlyRate?: number;
	availableBefore?: string;
}

/**
 * Talent profile details as returned by the API.
 * Note: Email is NOT included - companies should use the Contact feature.
 */
export interface TalentDetails {
	id: string;
	initials: string;
	hourlyRate: number;
	availableFrom: string | null;
	location: string | null;
	isAvailableForTravel: boolean;
	bioHtml: string;
	currentTitle?: string;
	skills?: string[];
	tags?: string[];
	personalStatement: string;
	whatLookingFor: string;
	// Media fields
	profilePictureUrl?: string;
	profilePictureThumbnailUrl?: string;
	introductionVideoUrl?: string;
	introductionVideoThumbnailUrl?: string;
	introductionVideoDurationSeconds?: number;
	// Social links and portfolio gallery
	socialLinks?: SocialLink[];
	mediaGallery?: TalentMediaItem[];
}

/**
 * Public talent profile - includes view permission flags.
 * Used for shareable profile links accessible by any authenticated user.
 */
export interface PublicTalentProfile extends TalentDetails {
	/** True if the requesting user owns this profile */
	isOwner: boolean;
	/** True if the requesting user can perform business actions (company users) */
	canPerformBusinessActions: boolean;
}

/**
 * Open Graph metadata for social sharing previews.
 */
export interface OpenGraphMetadata {
	title: string;
	description: string;
	url: string;
	imageUrl?: string;
	siteName: string;
	type: string;
}

export interface TalentProfile {
	id: string;
	details: TalentDetails;
	reason: string;
}

export interface SearchResponse {
	message: string;
	profiles: TalentProfile[];
}

// Backend AI Search Response types
interface AISearchResult {
	profileId: string;
	name: string;
	title?: string;
	matchScore: number;
	dailyRate?: number;
	hourlyRate?: number;
	availableFrom?: string;
	city?: string;
	workModes: string[];
	skills: string[];
	seniority?: string;
	profilePictureUrl?: string;
	profilePictureThumbnailUrl?: string;
	introductionVideoUrl?: string;
	introductionVideoThumbnailUrl?: string;
	introductionVideoDurationSeconds?: number;
	personalStatement?: string;
	whatLookingFor?: string;
	explanation?: {
		summary: string;
		factors: Array<{ factor: string; contribution: string; score: number }>;
	};
}

interface AISearchResponse {
	success: boolean;
	error?: string;
	query?: {
		original: string;
		language: string;
		parsed?: object;
	};
	results: {
		total: number;
		returned: number;
		limitedByPlan: boolean;
		items: AISearchResult[];
	};
	suggestions?: object;
	tokensUsed: number;
}

export interface SendTalentMessageRequest {
	subject: string;
	body: string;
}

const talentService = {
	// Search talents with AI-powered natural language query
	searchTalentsWithAI: async (organizationId: string, query: string): Promise<SearchResponse> => {
		try {
			const response = await apiClient.post<AISearchResponse>(
				`/api/organizations/${organizationId}/talents/search/ai`,
				{
					query: query,
					includeExplanations: true,
				},
			);

			const data = response.data;

			if (!data.success) {
				throw new Error(data.error || 'Search failed');
			}

			// Map AI search results to the expected SearchResponse format
			const profiles: TalentProfile[] = data.results.items.map((item) => ({
				id: item.profileId,
				details: {
					id: item.profileId,
					initials: item.name,
					hourlyRate: item.hourlyRate || 0,
					availableFrom: item.availableFrom || null,
					location: item.city || null,
					isAvailableForTravel: false,
					bioHtml: '',
					currentTitle: item.title,
					skills: item.skills,
					tags: item.workModes,
					personalStatement: item.personalStatement || '',
					whatLookingFor: item.whatLookingFor || '',
					profilePictureUrl: item.profilePictureUrl,
					profilePictureThumbnailUrl: item.profilePictureThumbnailUrl,
					introductionVideoUrl: item.introductionVideoUrl,
					introductionVideoThumbnailUrl: item.introductionVideoThumbnailUrl,
					introductionVideoDurationSeconds: item.introductionVideoDurationSeconds,
				},
				reason: item.explanation?.summary || '',
			}));

			// Generate a summary message
			const message =
				data.results.total > 0
					? `Ho trovato ${data.results.total} profili che corrispondono alla tua ricerca${data.results.limitedByPlan ? ` (mostrati ${data.results.returned})` : ''}.`
					: 'Non ho trovato profili che corrispondono alla tua ricerca.';

			return {
				message,
				profiles,
			};
		} catch (error) {
			console.error('Error searching talents with AI:', error);
			throw error;
		}
	},

	// Legacy method - redirects to new AI search (sessionId is no longer needed)
	searchTalentsWithCopilot: async (
		organizationId: string,
		_sessionId: string,
		query: string,
	): Promise<SearchResponse> => {
		return talentService.searchTalentsWithAI(organizationId, query);
	},

	// Classic search with filters
	searchTalentsWithFilters: async (
		organizationId: string,
		filters: TalentSearchFilters,
	): Promise<TalentDetails[]> => {
		try {
			const response = await apiClient.post<TalentDetails[]>(
				`/api/organizations/${organizationId}/talents/search/classic`,
				filters,
			);
			return response.data;
		} catch (error) {
			console.error('Error searching talents with filters:', error);
			throw error;
		}
	},

	getTalentProfileById: async (
		organizationId: string,
		profileId: string,
	): Promise<TalentDetails> => {
		const response = await apiClient.get<TalentDetails>(
			`/api/organizations/${organizationId}/talents/${profileId}`,
		);
		return response.data;
	},

	sendMessageToTalent: async (
		organizationId: string,
		freelanceUserId: string,
		payload: SendTalentMessageRequest,
	): Promise<void> => {
		await apiClient.post(
			`/api/organizations/${organizationId}/talents/${freelanceUserId}/sendmessage`,
			payload,
		);
		// 204 → nessun return
	},

	// ==================== Public Profile APIs ====================

	/**
	 * Get a public talent profile by ID.
	 * Accessible by any authenticated user.
	 * Returns profile details along with permission flags (isOwner, canPerformBusinessActions).
	 */
	getPublicProfile: async (talentId: string): Promise<PublicTalentProfile> => {
		const response = await apiClient.get<PublicTalentProfile>(`/api/talents/${talentId}`);
		return response.data;
	},

	/**
	 * Get Open Graph metadata for a talent profile.
	 * Used for social media preview cards.
	 */
	getOpenGraphMetadata: async (talentId: string): Promise<OpenGraphMetadata> => {
		const response = await apiClient.get<OpenGraphMetadata>(`/api/talents/${talentId}/og`);
		return response.data;
	},

	// Generate a UUID for sessions without external libraries
	generateUUID: (): string => {
		// Implementation of RFC4122 version 4 compliant UUID generator
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
			const r = (Math.random() * 16) | 0;
			const v = c === 'x' ? r : (r & 0x3) | 0x8;
			return v.toString(16);
		});
	},
};

export default talentService;
